import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Serviço para gerenciamento de imagens no Firebase Storage
 */

// Upload de imagem para o Firebase Storage
export const uploadImage = async (file, folder = 'rewards') => {
  try {
    // Validar arquivo
    if (!file) {
      throw new Error('Nenhum arquivo selecionado');
    }

    // Validar se é realmente um arquivo
    if (!(file instanceof File) && !(file instanceof Blob)) {
      throw new Error('Arquivo inválido');
    }

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de arquivo não suportado. Use JPG, PNG ou WebP.');
    }

    // Validar tamanho (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('Arquivo muito grande. Tamanho máximo: 5MB');
    }

    // Gerar nome único para o arquivo
    const timestamp = Date.now();
    const originalName = file.name || `image.${file.type.split('/')[1] || 'jpg'}`;
    const fileName = `${timestamp}_${originalName.replace(/[^a-zA-Z0-9.]/g, '_')}`;
    const filePath = `${folder}/${fileName}`;

    // Criar referência no Storage
    const imageRef = ref(storage, filePath);

    // Upload do arquivo
    const snapshot = await uploadBytes(imageRef, file);
    
    // Obter URL de download
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      path: filePath,
      fileName: fileName,
      size: file.size,
      type: file.type
    };
  } catch (error) {
    console.error('Erro no upload da imagem:', error);
    throw new Error('Erro no upload da imagem: ' + error.message);
  }
};

// Deletar imagem do Firebase Storage
export const deleteImage = async (imagePath) => {
  try {
    if (!imagePath) {
      throw new Error('Caminho da imagem não fornecido');
    }

    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
    
    return true;
  } catch (error) {
    console.error('Erro ao deletar imagem:', error);
    // Não lançar erro para não quebrar o fluxo se a imagem não existir
    return false;
  }
};

// Redimensionar imagem no cliente (opcional, para melhor performance)
export const resizeImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calcular novas dimensões mantendo proporção
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;

      // Desenhar e redimensionar
      ctx.drawImage(img, 0, 0, width, height);

      // Converter para blob
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          // Se falhar na conversão, usar arquivo original
          resolve(file);
        }
      }, file.type, quality);
    };

    img.src = URL.createObjectURL(file);
  });
};

// Validar se é uma URL de imagem válida
export const isValidImageUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:' && url.includes('firebasestorage.googleapis.com');
  } catch {
    return false;
  }
};

// Gerar placeholder para imagem
export const getImagePlaceholder = (width = 300, height = 200) => {
  return `data:image/svg+xml;base64,${btoa(`
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#f3f4f6"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#9ca3af" font-family="Arial, sans-serif" font-size="14">
        Imagem do Prêmio
      </text>
    </svg>
  `)}`;
};
